// src/admin/AdminReferralRebalance.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StopIcon from '@mui/icons-material/Stop';
import AdminPageWrapper from './components/AdminPageWrapper';
import api from '../utils/api';
import { API } from '../config/apiConfig';

const AUTO_DELAY_MS = 1800;
/** Stop placement auto-run only after this many consecutive "no candidate" responses (transient gaps / constraint stalls). */
const PLACEMENT_AUTO_MAX_EMPTY_STREAK = 8;

const SS_PLACEMENT_ROOT = 'adminPlacementRebuild.rootUserId';
const ssPlacementTotalKey = (rootId) => `adminPlacementRebuild.placementTotal.${rootId}`;

function parseRootId(raw) {
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readPlacementTotalFromStorage(rootId) {
  if (typeof window === 'undefined' || rootId == null) return 0;
  try {
    const v = parseInt(sessionStorage.getItem(ssPlacementTotalKey(rootId)) || '0', 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch {
    return 0;
  }
}

/** Increments browser-session tally for this root (each successful apply). Server state is authoritative; this is UX only. */
function bumpPlacementTotalInStorage(rootId) {
  if (typeof window === 'undefined' || rootId == null) return 0;
  const next = readPlacementTotalFromStorage(rootId) + 1;
  try {
    sessionStorage.setItem(ssPlacementTotalKey(rootId), String(next));
  } catch {
    /* ignore quota */
  }
  return next;
}

function readInitialPlacementRoot() {
  if (typeof window === 'undefined') return '7';
  try {
    const s = sessionStorage.getItem(SS_PLACEMENT_ROOT);
    const rid = parseRootId(s);
    if (rid != null) return String(rid);
  } catch {
    /* ignore */
  }
  return '7';
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value ?? '—'}</Typography>
    </Box>
  );
}

/** Called from lazy state init only in browser */
function initialPlacementLifetimeTotal() {
  return readPlacementTotalFromStorage(parseRootId(readInitialPlacementRoot()));
}

/**
 * Wait until browser reports online (event or navigator.onLine), polling every 3s.
 * Resolves 'stopped' if shouldContinue returns false (user clicked Stop).
 */
function waitForConnectivity(shouldContinue) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve('online');
      return;
    }
    let pollId;
    const cleanup = () => {
      window.removeEventListener('online', onOnline);
      if (pollId != null) clearInterval(pollId);
    };
    const finish = (reason) => {
      cleanup();
      resolve(reason);
    };
    const check = () => {
      if (!shouldContinue()) {
        finish('stopped');
        return;
      }
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        finish('online');
      }
    };
    function onOnline() {
      check();
    }
    window.addEventListener('online', onOnline);
    check();
    pollId = setInterval(check, 3000);
  });
}

export default function AdminReferralRebalance() {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [stats, setStats] = useState({ parentsWithTooMany: 0, usersToReallocate: 0 });
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoCount, setAutoCount] = useState(0);
  const runningRef = useRef(false);

  const [prRootUserId, setPrRootUserId] = useState(readInitialPlacementRoot);
  const [prPlacementLifetimeTotal, setPrPlacementLifetimeTotal] = useState(initialPlacementLifetimeTotal);
  const [prCandidate, setPrCandidate] = useState(null);
  const [prLoading, setPrLoading] = useState(false);
  const [prApplying, setPrApplying] = useState(false);
  const [prError, setPrError] = useState('');
  const [prSuccess, setPrSuccess] = useState('');
  const [prLoadedOnce, setPrLoadedOnce] = useState(false);
  const [prStats, setPrStats] = useState({
    misplacedCount: 0,
    totalMovable: 0,
    l1Count: 0,
    isEstimated: false,
    placedCorrectCount: null,
    downlineMemberCount: null,
    placementStructuralIssue: null,
    remainingAfterSimulation: null,
  });
  const [prAutoRunning, setPrAutoRunning] = useState(false);
  const [prAutoCount, setPrAutoCount] = useState(0);
  const [prPlacementCycleBusy, setPrPlacementCycleBusy] = useState(false);
  const [prAutoWaitingNetwork, setPrAutoWaitingNetwork] = useState(false);
  const [prAlignmentLoading, setPrAlignmentLoading] = useState(false);
  const prRunningRef = useRef(false);

  /** Persist root id + refresh session-only apply tally when root changes */
  useEffect(() => {
    const rid = parseRootId(prRootUserId);
    if (rid == null) return;
    try {
      sessionStorage.setItem(SS_PLACEMENT_ROOT, String(rid));
    } catch {
      /* ignore */
    }
    setPrPlacementLifetimeTotal(readPlacementTotalFromStorage(rid));
  }, [prRootUserId]);

  const fetchStats = useCallback(async () => {
    try {
      const url = API?.adminRebalanceStats ?? '/api/admin/rebalance/stats';
      const res = await api.get(url);
      const data = res?.data;
      if (process.env.NODE_ENV === 'development') console.log('[Rebalance] stats response', data);
      if (data?.success) {
        setStats({
          parentsWithTooMany: data.parentsWithTooMany ?? 0,
          usersToReallocate: data.usersToReallocate ?? 0,
        });
      } else {
        setStats({ parentsWithTooMany: 0, usersToReallocate: 0 });
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[Rebalance] stats failed', e?.response?.status, e?.response?.data);
      setStats({ parentsWithTooMany: 0, usersToReallocate: 0 });
    }
  }, []);

  const fetchPlacementStats = useCallback(async () => {
    const rid = parseRootId(prRootUserId);
    if (rid == null) {
      setPrStats({
        misplacedCount: 0,
        totalMovable: 0,
        l1Count: 0,
        isEstimated: false,
        placedCorrectCount: null,
        downlineMemberCount: null,
        placementStructuralIssue: null,
        remainingAfterSimulation: null,
      });
      return;
    }
    try {
      const url =
        (API?.adminPlacementRebuildStats && API.adminPlacementRebuildStats(rid, { includeAlignment: false })) ??
        `/api/admin/rebalance/placement-rebuild/stats?rootUserId=${encodeURIComponent(rid)}`;
      const res = await api.get(url);
      const data = res?.data;
      if (data?.success) {
        setPrStats({
          misplacedCount: data.misplacedCount ?? 0,
          totalMovable: data.totalMovable ?? 0,
          l1Count: data.l1Count ?? 0,
          isEstimated: !!data.isEstimated,
          placedCorrectCount: data.placedCorrectCount ?? null,
          downlineMemberCount: data.downlineMemberCount ?? null,
          placementStructuralIssue: data.placementStructuralIssue ?? null,
          remainingAfterSimulation: data.remainingAfterSimulation ?? null,
        });
      } else {
        setPrStats({
          misplacedCount: 0,
          totalMovable: 0,
          l1Count: 0,
          isEstimated: false,
          placedCorrectCount: null,
          downlineMemberCount: null,
          placementStructuralIssue: null,
          remainingAfterSimulation: null,
        });
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[PlacementRebuild] stats failed', e?.response?.data);
      setPrStats({
        misplacedCount: 0,
        totalMovable: 0,
        l1Count: 0,
        isEstimated: false,
        placedCorrectCount: null,
        downlineMemberCount: null,
        placementStructuralIssue: null,
        remainingAfterSimulation: null,
      });
    }
  }, [prRootUserId]);

  /** Full alignment simulation — can take many minutes; separate from fast Refresh stats. */
  const fetchPlacementAlignmentStats = useCallback(async () => {
    const rid = parseRootId(prRootUserId);
    if (rid == null) {
      setPrError('Enter a valid root User ID (positive integer).');
      return;
    }
    setPrAlignmentLoading(true);
    setPrError('');
    try {
      const url =
        (API?.adminPlacementRebuildStats && API.adminPlacementRebuildStats(rid, { includeAlignment: true })) ??
        `/api/admin/rebalance/placement-rebuild/stats?rootUserId=${encodeURIComponent(rid)}&includeAlignment=1`;
      const res = await api.get(url);
      const data = res?.data;
      if (data?.success) {
        setPrStats((prev) => ({
          ...prev,
          misplacedCount: data.misplacedCount ?? prev.misplacedCount,
          totalMovable: data.totalMovable ?? prev.totalMovable,
          l1Count: data.l1Count ?? prev.l1Count,
          isEstimated: !!data.isEstimated,
          placedCorrectCount: data.placedCorrectCount ?? null,
          downlineMemberCount: data.downlineMemberCount ?? null,
          placementStructuralIssue: data.placementStructuralIssue ?? null,
          remainingAfterSimulation: data.remainingAfterSimulation ?? null,
        }));
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Request failed';
      setPrError(msg === 'Network Error' ? `${msg} — alignment query may still be running; check API/SQL timeouts.` : msg);
    } finally {
      setPrAlignmentLoading(false);
    }
  }, [prRootUserId]);

  const fetchNext = useCallback(async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const url = API?.adminRebalanceNext ?? '/api/admin/rebalance/next';
      const res = await api.get(url);
      const data = res?.data;
      if (process.env.NODE_ENV === 'development') console.log('[Rebalance] next response', { success: data?.success, hasCandidate: !!data?.candidate, error: data?.error });
      if (data?.success) {
        setCandidate(data.candidate ?? null);
        if (!loadedOnce) setLoadedOnce(true);
        await fetchStats();
      } else {
        setError(data?.error || 'Failed to fetch next candidate');
        setCandidate(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Request failed';
      const status = err?.response?.status;
      if (process.env.NODE_ENV === 'development') console.warn('[Rebalance] next failed', status, err?.response?.data);
      setError(status === 403 ? 'Admin access required. Log in as admin.' : msg);
      setCandidate(null);
    } finally {
      setLoading(false);
    }
  }, [loadedOnce, fetchStats]);

  const fetchPlacementNext = useCallback(async () => {
    const rid = parseRootId(prRootUserId);
    if (rid == null) {
      setPrError('Enter a valid root User ID (positive integer).');
      return;
    }
    setPrError('');
    setPrSuccess('');
    setPrLoading(true);
    try {
      const url =
        (API?.adminPlacementRebuildNext && API.adminPlacementRebuildNext(rid)) ??
        `/api/admin/rebalance/placement-rebuild/next?rootUserId=${rid}`;
      const res = await api.get(url);
      const data = res?.data;
      if (data?.success) {
        setPrCandidate(data.candidate ?? null);
        if (!prLoadedOnce) setPrLoadedOnce(true);
        await fetchPlacementStats();
      } else {
        setPrError(data?.error || 'Failed to fetch next candidate');
        setPrCandidate(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Request failed';
      const status = err?.response?.status;
      setPrError(status === 403 ? 'Admin access required. Log in as admin.' : msg);
      setPrCandidate(null);
    } finally {
      setPrLoading(false);
    }
  }, [prRootUserId, prLoadedOnce, fetchPlacementStats]);

  const handleApply = async () => {
    if (!candidate?.UserID || !candidate?.ProposedNewParentReferralCode) {
      setError('Missing user or new parent');
      return;
    }
    setError('');
    setSuccess('');
    setApplying(true);
    try {
      const url = API?.adminRebalanceApply ?? '/api/admin/rebalance/apply';
      await api.post(url, {
        userId: candidate.UserID,
        newReferredByCode: candidate.ProposedNewParentReferralCode,
      });
      setSuccess('Reallocation applied. Fetching next…');
      setCandidate(null);
      await fetchStats();
      await fetchNext();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Apply failed';
      setError(msg);
    } finally {
      setApplying(false);
    }
  };

  const handlePlacementApply = async () => {
    if (!prCandidate?.UserID || !prCandidate?.ProposedNewParentReferralCode) {
      setPrError('Missing user or new parent');
      return;
    }
    setPrError('');
    setPrSuccess('');
    setPrApplying(true);
    try {
      const url = API?.adminPlacementRebuildApply ?? '/api/admin/rebalance/placement-rebuild/apply';
      await api.post(url, {
        userId: prCandidate.UserID,
        newReferredByCode: prCandidate.ProposedNewParentReferralCode,
      });
      const rid = parseRootId(prRootUserId);
      const sessionTotal = rid != null ? bumpPlacementTotalInStorage(rid) : 0;
      if (rid != null) setPrPlacementLifetimeTotal(sessionTotal);
      setPrSuccess(
        `Placement updated (OriginalReferredByCode unchanged). Applies in this tab for root ${rid ?? '—'}: ${sessionTotal}. Fetching next…`,
      );
      setPrCandidate(null);
      await fetchPlacementStats();
      await fetchPlacementNext();
    } catch (err) {
      setPrError(err?.response?.data?.error || err?.message || 'Apply failed');
    } finally {
      setPrApplying(false);
    }
  };

  const runOneCycle = useCallback(async () => {
    try {
      const nextUrl = API?.adminRebalanceNext ?? '/api/admin/rebalance/next';
      const res = await api.get(nextUrl);
      const data = res?.data;
      const c = data?.candidate ?? null;
      setCandidate(c);
      if (!c?.UserID || !c?.ProposedNewParentReferralCode) return 'empty';
      const applyUrl = API?.adminRebalanceApply ?? '/api/admin/rebalance/apply';
      await api.post(applyUrl, {
        userId: c.UserID,
        newReferredByCode: c.ProposedNewParentReferralCode,
      });
      await fetchStats();
      return 'applied';
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Auto cycle failed');
      return 'error';
    }
  }, [fetchStats]);

  const runPlacementOneCycle = useCallback(async () => {
    const rid = parseRootId(prRootUserId);
    if (rid == null) return { status: 'invalid' };
    setPrPlacementCycleBusy(true);
    try {
      const nextUrl =
        (API?.adminPlacementRebuildNext && API.adminPlacementRebuildNext(rid)) ??
        `/api/admin/rebalance/placement-rebuild/next?rootUserId=${rid}`;
      const res = await api.get(nextUrl);
      const data = res?.data;
      if (data?.success) {
        setPrLoadedOnce(true);
      }
      const c = data?.candidate ?? null;
      setPrCandidate(c);
      if (!c?.UserID || !c?.ProposedNewParentReferralCode) return { status: 'empty' };
      const applyUrl = API?.adminPlacementRebuildApply ?? '/api/admin/rebalance/placement-rebuild/apply';
      await api.post(applyUrl, {
        userId: c.UserID,
        newReferredByCode: c.ProposedNewParentReferralCode,
      });
      const sessionTotal = bumpPlacementTotalInStorage(rid);
      setPrPlacementLifetimeTotal(sessionTotal);
      await fetchPlacementStats();
      return { status: 'applied' };
    } catch (err) {
      setPrError(err?.response?.data?.error || err?.message || 'Auto cycle failed');
      return { status: 'error' };
    } finally {
      setPrPlacementCycleBusy(false);
    }
  }, [prRootUserId, fetchPlacementStats]);

  const handleStartAuto = useCallback(async () => {
    setError('');
    setSuccess('');
    setAutoCount(0);
    runningRef.current = true;
    setAutoRunning(true);
    let count = 0;
    while (runningRef.current) {
      const cycle = await runOneCycle();
      if (!runningRef.current) break;
      if (cycle === 'applied') {
        count += 1;
        setAutoCount(count);
        await new Promise((r) => setTimeout(r, AUTO_DELAY_MS));
        continue;
      }
      if (cycle === 'empty') break;
      if (cycle === 'error') {
        await waitForConnectivity(() => runningRef.current);
        if (!runningRef.current) break;
        setError('');
        await new Promise((r) => setTimeout(r, 500));
        continue;
      }
      break;
    }
    setAutoRunning(false);
    setCandidate(null);
    await fetchStats();
    await fetchNext();
    setSuccess(`Auto run stopped. ${count} reallocation(s) applied.`);
  }, [runOneCycle, fetchStats, fetchNext]);

  const handlePlacementStartAuto = useCallback(async () => {
    setPrError('');
    setPrSuccess('');
    setPrAutoCount(0);
    prRunningRef.current = true;
    setPrAutoRunning(true);
    let count = 0;
    let emptyStreak = 0;
    let stoppedAfterRepeatedEmpty = false;
    while (prRunningRef.current) {
      const cycle = await runPlacementOneCycle();
      if (!prRunningRef.current) break;
      if (cycle.status === 'applied') {
        emptyStreak = 0;
        count += 1;
        setPrAutoCount(count);
        await new Promise((r) => setTimeout(r, AUTO_DELAY_MS));
        continue;
      }
      if (cycle.status === 'empty') {
        emptyStreak += 1;
        if (emptyStreak >= PLACEMENT_AUTO_MAX_EMPTY_STREAK) {
          stoppedAfterRepeatedEmpty = true;
          break;
        }
        await new Promise((r) => setTimeout(r, AUTO_DELAY_MS * 2));
        continue;
      }
      if (cycle.status === 'error') {
        if (!prRunningRef.current) break;
        setPrAutoWaitingNetwork(true);
        setPrError(
          (prev) =>
            prev ||
            'Network or server error — Auto will continue when the connection is back (or click Stop).',
        );
        const why = await waitForConnectivity(() => prRunningRef.current);
        setPrAutoWaitingNetwork(false);
        if (why === 'stopped' || !prRunningRef.current) break;
        setPrError('');
        await new Promise((r) => setTimeout(r, 500));
        continue;
      }
      break;
    }
    prRunningRef.current = false;
    setPrAutoRunning(false);
    setPrCandidate(null);
    await fetchPlacementStats();
    await fetchPlacementNext();
    const ridFinal = parseRootId(prRootUserId);
    const tabTotal = ridFinal != null ? readPlacementTotalFromStorage(ridFinal) : 0;
    if (count > 0) {
      setPrSuccess(
        stoppedAfterRepeatedEmpty
          ? `Auto run stopped. ${count} placement update(s) in this Auto run (not from zero — each apply was already saved). Tab session total for root ${ridFinal ?? '—'}: ${tabTotal}. No candidate returned ${PLACEMENT_AUTO_MAX_EMPTY_STREAK} times in a row — try Refresh next/stats, or the tree may have no valid move.`
          : `Auto run stopped. ${count} placement update(s) in this Auto run. Tab session total for root ${ridFinal ?? '—'}: ${tabTotal}. Stopping or starting Auto does not undo work — next run continues from the database.`,
      );
    } else if (stoppedAfterRepeatedEmpty) {
      setPrSuccess(
        `Auto run stopped: no candidate after ${PLACEMENT_AUTO_MAX_EMPTY_STREAK} tries — see message above or Refresh stats.`,
      );
    } else {
      setPrSuccess('Auto run stopped (no moves applied).');
    }
  }, [runPlacementOneCycle, fetchPlacementStats, fetchPlacementNext]);

  const handleStopAuto = useCallback(() => {
    runningRef.current = false;
  }, []);

  const handlePlacementStopAuto = useCallback(() => {
    prRunningRef.current = false;
  }, []);

  React.useEffect(() => { fetchStats(); }, [fetchStats]);

  React.useEffect(() => {
    const rid = parseRootId(prRootUserId);
    if (rid != null) fetchPlacementStats();
  }, [prRootUserId, fetchPlacementStats]);

  const placementCandidateCard = prCandidate && (
    <Card variant="outlined" sx={{ mt: 1, overflow: 'visible' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
          Level-fill: parent A → parent B
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Moves one user so <strong>ReferredByCode</strong> matches the BFS binary target under the root (L1 fixed; max 2 per parent).
          <strong> OriginalReferredByCode is not changed.</strong>
        </Typography>

        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PersonIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>User to move</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <DetailRow label="User ID" value={prCandidate.UserID} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DetailRow label="Username" value={prCandidate.UserName} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DetailRow label="Email" value={prCandidate.Email} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DetailRow label="Referral code" value={prCandidate.ReferralCode} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%', borderColor: 'error.light', borderWidth: 1 }}>
              <CardContent>
                <Chip label="Parent A (current)" size="small" color="error" sx={{ mb: 1.5 }} />
                <DetailRow label="Username" value={prCandidate.CurrentParentUserName} />
                <DetailRow label="Referral code" value={prCandidate.CurrentParentReferralCode} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowForwardIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%', borderColor: 'success.main', borderWidth: 1 }}>
              <CardContent>
                <Chip label="Parent B (target)" size="small" color="success" sx={{ mb: 1.5 }} />
                <DetailRow label="Username" value={prCandidate.ProposedNewParentUserName} />
                <DetailRow label="Referral code" value={prCandidate.ProposedNewParentReferralCode} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          startIcon={prApplying ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          onClick={handlePlacementApply}
          disabled={prApplying || prAutoRunning}
        >
          {prApplying ? 'Applying…' : 'Apply placement (ReferredByCode only)'}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <AdminPageWrapper
      title="Referral tree rebalance"
      subtitle="Overflow fix (3+ direct children) and level-fill rebuild (L1 fixed, BFS binary) — semi-automatic"
    >
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={700}>1. More than 2 direct children</Typography>
            <Typography variant="body2" color="text.secondary">
              Moves 3rd+ direct referrals using the existing slot finder. Updates OriginalReferredByCode on apply.
            </Typography>

            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

            <Alert severity={stats.usersToReallocate > 0 ? 'warning' : 'info'} sx={{ mb: 1 }}>
              <strong>Status:</strong> {stats.parentsWithTooMany} parent(s) have more than 2 direct children.{' '}
              <strong>{stats.usersToReallocate} user(s)</strong> still need reallocation.
              {stats.usersToReallocate > 0 && ' Keep clicking "Get next candidate" and "Apply" until this reaches 0.'}
              {stats.parentsWithTooMany === 0 && stats.usersToReallocate === 0 && (
                <Typography component="span" display="block" sx={{ mt: 1, fontSize: '0.85rem' }}>
                  If SSMS shows data but this shows 0, the API may be using a different database. Use <code>npm run start:local</code> and ensure the API .env.local points to the same DB as SSMS.
                </Typography>
              )}
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={fetchNext}
                disabled={loading || autoRunning || prAutoRunning}
              >
                {loadedOnce ? 'Refresh next' : 'Get next candidate'}
              </Button>
              <Button variant="outlined" size="small" onClick={fetchStats} disabled={autoRunning || prAutoRunning}>Refresh stats</Button>
              {!autoRunning ? (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartAuto}
                  disabled={loading || applying || stats.usersToReallocate === 0 || prAutoRunning}
                >
                  Run automatically
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStopAuto}
                >
                  Stop ({autoCount} done)
                </Button>
              )}
            </Box>
            {autoRunning && (
              <Alert severity="info">
                Auto-running: applying each reallocation every ~{AUTO_DELAY_MS / 1000}s. Click <strong>Stop</strong> to end.
              </Alert>
            )}

            {loading && !candidate && !loadedOnce && (
              <Typography color="text.secondary">Loading…</Typography>
            )}

            {!loading && loadedOnce && !candidate && (
              <Alert severity="info">
                {stats.usersToReallocate === 0
                  ? 'No more users to reallocate.'
                  : `No candidate returned but ${stats.usersToReallocate} user(s) still need reallocation. You may be hitting "No available slot" (tree full under current rules).`}
              </Alert>
            )}

            {candidate && (
              <Card variant="outlined" sx={{ mt: 1, overflow: 'visible' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    Change placement: parent A → parent B
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The user below will be moved so their placement parent changes from <strong>Parent A</strong> (current) to <strong>Parent B</strong> (new). User and referral link stay the same; only the tree position changes.
                  </Typography>

                  <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PersonIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>User to reallocate</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <DetailRow label="User ID" value={candidate.UserID} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <DetailRow label="Username" value={candidate.UserName} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <DetailRow label="Email" value={candidate.Email} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <DetailRow label="Referral code" value={candidate.ReferralCode} />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
                    <Grid item xs={12} md={5}>
                      <Card variant="outlined" sx={{ height: '100%', borderColor: 'error.light', borderWidth: 1 }}>
                        <CardContent>
                          <Chip label="Parent A (current)" size="small" color="error" sx={{ mb: 1.5 }} />
                          <DetailRow label="Username" value={candidate.CurrentParentUserName} />
                          <DetailRow label="Referral code" value={candidate.CurrentParentReferralCode} />
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            Current placement parent — will be removed
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ArrowForwardIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Card variant="outlined" sx={{ height: '100%', borderColor: 'success.main', borderWidth: 1 }}>
                        <CardContent>
                          <Chip label="Parent B (new)" size="small" color="success" sx={{ mb: 1.5 }} />
                          <DetailRow label="Username" value={candidate.ProposedNewParentUserName} />
                          <DetailRow label="Referral code" value={candidate.ProposedNewParentReferralCode} />
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            New placement parent — user will be placed here
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={applying ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                    onClick={handleApply}
                    disabled={applying || autoRunning || prAutoRunning}
                  >
                    {applying ? 'Applying…' : 'Apply: change parent from A to B'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Paper>

        <Divider />

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={700}>2. Level-fill under root (L1 fixed)</Typography>
            <Typography variant="body2" color="text.secondary">
              Computes a breadth-first binary target (L1 fixed; queue seeded with L1 by UserID; movable users by CreatedAt).
              Honours <strong>OriginalReferredByCode</strong>: each user’s placement parent must sit under their original sponsor’s
              branch (the sponsor is an ancestor of the new parent toward the root). Applies <strong>ReferredByCode</strong> only —
              <strong>OriginalReferredByCode</strong> is not modified.
            </Typography>

            <Alert severity="info" sx={{ py: 0.75 }}>
              <strong>No “reset to user 1”:</strong> every apply is written to the database immediately. After you stop or refresh,
              the next <strong>Get next</strong> or <strong>Auto</strong> asks the server for whoever still needs a move — you never redo
              completed updates. The <strong>moves this Auto run</strong> counter is only for this run; <strong>Applies in this tab</strong>{' '}
              (below) tracks how many applies this browser tab has recorded for this root during the session.
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Root User ID"
                type="number"
                size="small"
                value={prRootUserId}
                onChange={(e) => setPrRootUserId(e.target.value)}
                sx={{ width: 160 }}
                disabled={prAutoRunning || autoRunning}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 1 } }}>
                Applies in this tab (session, root {parseRootId(prRootUserId) ?? '—'}):{' '}
                <strong>{prPlacementLifetimeTotal}</strong>
              </Typography>
            </Box>

            {prError && <Alert severity="error" onClose={() => setPrError('')}>{prError}</Alert>}
            {prSuccess && <Alert severity="success" onClose={() => setPrSuccess('')}>{prSuccess}</Alert>}

            <Alert severity={prStats.misplacedCount > 0 ? 'warning' : 'info'}>
              <strong>Status:</strong> L1 count = {prStats.l1Count}. Movable (non-L1) downline = {prStats.totalMovable}.{' '}
              <strong>{prStats.misplacedCount}</strong>
              {prStats.isEstimated ? ' (upper-bound estimate)' : ''} placement(s) still differ from the BFS target.
              {prStats.downlineMemberCount != null && (
                <Typography component="span" display="block" sx={{ mt: 1 }}>
                  <strong>Placed correctly (database vs full BFS target):</strong>{' '}
                  {prStats.placedCorrectCount != null ? prStats.placedCorrectCount : '—'} of {prStats.downlineMemberCount}{' '}
                  original-tree members under this root.
                  {prStats.placementStructuralIssue ? (
                    <> Structural issue (no L1 while deeper original downline exists) — alignment count unavailable.</>
                  ) : null}
                  {prStats.remainingAfterSimulation != null && prStats.remainingAfterSimulation > 0 ? (
                    <>
                      {' '}
                      Simulation left {prStats.remainingAfterSimulation} user(s) unassigned — alignment count may be incomplete.
                    </>
                  ) : null}
                </Typography>
              )}
              {prStats.downlineMemberCount == null && !prStats.isEstimated && (
                <Typography component="span" display="block" sx={{ mt: 0.75 }} variant="body2" color="text.secondary">
                  Optional: click <strong>Global placement count (slow)</strong> below for X-of-Y counts (runs full SQL simulation;
                  requires <code>process.mlm_GetPlacementRebuildCorrectCount</code> on the server).
                </Typography>
              )}
              {prStats.l1Count === 0 && prStats.totalMovable > 0 && (
                <Typography component="span" display="block" sx={{ mt: 1 }}>
                  Root has no direct referrals but downline exists — fix data first.
                </Typography>
              )}
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={prLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={fetchPlacementNext}
                disabled={prLoading || prAutoRunning || autoRunning || prAlignmentLoading}
              >
                {prLoadedOnce ? 'Refresh next' : 'Get next candidate'}
              </Button>
              <Button variant="outlined" size="small" onClick={fetchPlacementStats} disabled={prAutoRunning || autoRunning || prAlignmentLoading}>Refresh stats</Button>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={fetchPlacementAlignmentStats}
                disabled={prAutoRunning || autoRunning || prAlignmentLoading}
                startIcon={prAlignmentLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                Global placement count (slow)
              </Button>
              {!prAutoRunning ? (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PlayArrowIcon />}
                  onClick={handlePlacementStartAuto}
                  disabled={prLoading || prApplying || prStats.misplacedCount === 0 || autoRunning || prAlignmentLoading}
                >
                  Run automatically
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handlePlacementStopAuto}
                >
                  Stop (this Auto run: {prAutoCount})
                </Button>
              )}
            </Box>
            {prAutoRunning && (
              <Alert severity="info" icon={prPlacementCycleBusy ? <CircularProgress size={20} color="inherit" /> : undefined}>
                {prAutoWaitingNetwork ? (
                  <>
                    <strong>Waiting for connectivity</strong> — the last request failed (often a network glitch). Auto will
                    retry when the browser reports you are online again; click <strong>Stop</strong> to cancel.
                  </>
                ) : prPlacementCycleBusy ? (
                  <>
                    <strong>Waiting on the server</strong> — the next-candidate request runs a large SQL routine. The first
                    call can take many minutes; <strong>done</strong> only increases after a move is applied, not while SQL is
                    still computing. If the API used a short SQL timeout, fix <code>SQL_REQUEST_TIMEOUT_MS</code> (use{' '}
                    <code>0</code> for no limit) and redeploy the API.
                  </>
                ) : (
                  <>
                    Auto-running placement updates every ~{AUTO_DELAY_MS / 1000}s. Brief disconnects are retried automatically
                    when the connection returns. Click <strong>Stop</strong> to end.
                  </>
                )}
              </Alert>
            )}

            {(prLoading || (prAutoRunning && prPlacementCycleBusy)) && !prCandidate && !prLoadedOnce && (
              <Typography color="text.secondary">
                {prAutoRunning && prPlacementCycleBusy ? 'Request in progress (SQL may be busy for a long time)…' : 'Loading…'}
              </Typography>
            )}

            {!prLoading && prLoadedOnce && !prCandidate && (
              <Alert severity="info">
                {prStats.misplacedCount === 0
                  ? 'No misplaced placements for this target (or nothing to do).'
                  : `No candidate returned but ${prStats.misplacedCount}${prStats.isEstimated ? ' (estimate)' : ''} still look misplaced — try Refresh next/stats (count may not drop each move).`}
              </Alert>
            )}

            {placementCandidateCard}
          </Stack>
        </Paper>
      </Stack>
    </AdminPageWrapper>
  );
}
